import React, { FunctionComponent, useEffect, useState } from 'react';
import styles from './IconCard.module.scss';
import CardTitle from 'app/components/CardTitle';
import TimeIntervalFilter from '../TimeIntervalFilter';
import { nFormatter } from 'app/utils/numbersFormater';
import Loader from '../Loader';
import { FollowersCount, HashIcon } from 'app/assets/images/icons';
import { dateTimeFormatter } from 'app/utils/dateTimeFormatter';
import DotsMenu from '../DotsMenu';
import { getCardData } from 'influencers/Requests';
import { useQuery, useQueryClient } from 'react-query';
import Grid, { GridSize } from '@material-ui/core/Grid';
import ServerError from '../ErrorBoundary/ServerError';
import { checkDay } from 'influencers/components/AnalyedInfluencerCharts/AnalyedInfluencerCharts';

interface Props {
  title: string;
  compId: string;
  increaseHeight?: boolean;
  cardData?: {
    icon?: any;
    count?: number | string;
    newCount?: number | string;
    countName?: string;
    [key: string]: any;
  };
  slug?: string;
  withFilter?: boolean;
  hideInput?: boolean;
  customFilters?: any;
  hideCountName?: boolean;
  refetch?: any;
  showCountName?: boolean;
  fliptextdirection?: boolean;
  hideEmptyCount?: boolean;
  grid?: {
    xs?: GridSize;
    lg?: GridSize;
    md?: GridSize;
    sm?: GridSize;
    xl?: GridSize;
  };
  hideIfNoData?: boolean;
  additionalComponents?: JSX.Element;
  isReport?: boolean;
  hideDay?: boolean;
  isUpperCase?: boolean;
  isCapitalize?: boolean;
  useCheckDays?: boolean;
  hideCount?: boolean;
}

const IconCard: FunctionComponent<Props> = ({
  compId,
  cardData,
  withFilter,
  title,
  increaseHeight,
  hideInput,
  slug,
  customFilters,
  showCountName,
  refetch,
  fliptextdirection,
  grid,
  hideIfNoData,
  additionalComponents,
  isReport,
  hideDay,
  hideEmptyCount,
  isUpperCase,
  isCapitalize,
  useCheckDays,
  hideCount,
}) => {
  useEffect(() => {
    setFilters({ ...customFilters, time_interval: 'ALL' });
  }, [customFilters]);
  const [filters, setFilters] = useState({
    ...customFilters,
    time_interval: 'ALL',
  });
  const handleChangeFilter = (val: any) => {
    setFilters({ ...filters, time_interval: val });
  };

  const {
    data: QData,
    isLoading,
    isError,
  } = useQuery(['getCardData', { slug, body: filters }, refetch], getCardData, {
    enabled: !!slug,
  });
  const queryClient = useQueryClient();
  useEffect(() => {
    return () => {
      queryClient.removeQueries(['getCardData']);
    };
  }, []);

  const data = !!slug ? QData : cardData;
  if (
    (!data && hideIfNoData && !isLoading) ||
    (hideIfNoData && data?.countName === '-')
  )
    return <></>;
  return (
    <ServerError requiredApiCalls={[isError]}>
      <Grid item {...grid}>
        <div
          id={compId}
          data-testid="icon-card-holder"
          className="position-relative"
        >
          <CardTitle title={title} compId={compId} hideIcon={isReport} />
          <div
            data-testid="cardContent"
            className={`${styles.IconCard} ${
              increaseHeight ? styles.RowHeight : null
            } `}
            id={`${compId}-IconCard`}
          >
            <div
              className={`${styles.IconCard__CardHead} ${
                withFilter
                  ? styles.IconCard__CardHeadFilter
                  : styles.IconCard__OnlyExport
              }`}
            >
              {!withFilter ? (
                <div className={styles.Actions}>
                  {!isReport && <DotsMenu id={compId} />}
                </div>
              ) : (
                <TimeIntervalFilter
                  compId={compId}
                  hideInput={hideInput}
                  handleChangeTime={handleChangeFilter}
                  allTimeInterval
                  isReport={isReport}
                />
              )}
            </div>
            <div
              className={styles.IconCard__Icon}
              id={`${compId}-IconCard-icon`}
            >
              {cardData?.icon || <HashIcon />}
            </div>
            {isLoading ? (
              <Loader />
            ) : (
              <div className={styles.IconCard__CountHolder}>
                <p
                  className={`${
                    showCountName || hideEmptyCount
                      ? styles.IconCard__SmallCount
                      : styles.IconCard__Count
                  } ${fliptextdirection && styles.LeftToRight} ${isUpperCase && styles.IconCard__upperCase}  ${isCapitalize && styles.IconCard__CapitalizeCase}`}
                  data-testid="count-holder"
                  id={`${compId}-IconCard-count`}
                >
                  {data &&
                  typeof data?.count == 'string' &&
                  !hideEmptyCount &&
                  !hideCount &&
                  data?.count?.length > 5
                    ? data?.count === 'noShow' || cardData?.count === 'noShow'
                      ? ''
                      : data?.count?.includes('-') && data?.count?.includes('Z')
                        ? dateTimeFormatter?.date(
                            data?.count,
                            undefined,
                            undefined,
                            hideDay,
                          )
                        : typeof data?.count === 'string' && data?.count
                    : hideEmptyCount
                      ? '-'
                      : hideCount
                        ? ''
                        : useCheckDays
                          ? checkDay(nFormatter(data?.count))
                          : nFormatter(
                              typeof data?.count === 'number'
                                ? data?.count?.toFixed(2)
                                : data?.count,
                            ) ||
                            nFormatter(cardData?.count) ||
                            0}
                  {showCountName && `  ${data?.countName}`}
                </p>
                {data && data?.newCount > 0 && (
                  <>
                    <span data-testid="new-count-holder">
                      {nFormatter(data?.newCount) || 0}
                    </span>
                    <FollowersCount className={styles.IconCard__Icon} />
                  </>
                )}
                {data && data?.newCount < 0 && (
                  <>
                    <span
                      data-testid="new-count-holder"
                      className={styles.IconCard__followerDecrease}
                    >
                      {nFormatter(Math.abs(data?.newCount)) || 0}
                    </span>
                    <FollowersCount
                      className={`${styles.IconCard__Icon} ${styles.IconCard__followerIcon}`}
                    />
                  </>
                )}
              </div>
            )}
          </div>
          {additionalComponents}
        </div>
      </Grid>
    </ServerError>
  );
};

export default IconCard;
